import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import BravosSurveyFlow from './BravosSurveyFlow';
import WorkingOnFeedback from './WorkingOnFeedback';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* Bravos assessment (member, authenticated) — the paginated flow used across ALL member
   assessments (employee/executive pulse + culture survey), matching the public pulse UX: one
   question per screen with quote/visual cards interleaved, an "AI working on your feedback"
   animation, instant results, and an optional "email me my results" (to the member's own
   address — no captcha, they're logged in). A prior submission re-opens as "see results".
   Backed by GET /questions (questions + interstitials), POST /submit, GET /result, and
   POST /email-results (routes/bravos.py). */

const MIN_WORKING_MS = 7000;   // let the "working on your feedback" animation play out

const BravosSurvey = () => {
  const { t } = useTranslation();
  const { key } = useParams();
  const [meta, setMeta] = useState(null);
  const [stage, setStage] = useState('loading');   // loading | questions | working | results
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);
  const [removing, setRemoving] = useState(false);

  const loadQuestions = useCallback(async () => {
    const m = await apiRequest('GET', `/bravos/instrument/${key}/questions`);
    setMeta(m);
    return m;
  }, [key]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Already completed? Show the member their OWN feedback again (recomputed from their stored
      // answers) — so "see results" works and a reload keeps the feedback.
      try {
        const prior = await apiRequest('GET', `/bravos/instrument/${key}/result`);
        if (!cancelled && prior && prior.feedback) {
          setResult(prior);
          setMeta({ name: prior.name });
          setJustSubmitted(false);
          setStage('results');
          return;
        }
      } catch { /* no prior result — fall through to the questionnaire */ }
      if (cancelled) return;
      try {
        await loadQuestions();
        if (!cancelled) setStage('questions');
      } catch {
        if (!cancelled) { setError(t('somethingWentWrong')); setStage('results'); }
      }
    })();
    return () => { cancelled = true; };
  }, [key, loadQuestions, t]);

  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const revealAfter = (started, fn) =>
    setTimeout(fn, Math.max(0, MIN_WORKING_MS - (Date.now() - started)));

  // After the last question: record + show the member's instant feedback.
  const computeResults = async () => {
    setError(null);
    setEmailed(false);
    setStage('working');
    const started = Date.now();
    try {
      const res = await apiRequest('POST', `/bravos/instrument/${key}/submit`, { answers });
      revealAfter(started, () => {
        setResult(res); setJustSubmitted(true); setStage('results'); window.scrollTo(0, 0);
      });
    } catch (err) {
      revealAfter(started, () => {
        setResult(null); setError(t(err.message, { defaultValue: err.message }));
        setStage('results'); window.scrollTo(0, 0);
      });
    }
  };

  // "Email me my results" — the member is authed, so this just mails their own feedback to their
  // own address (server recomputes from their stored answers). No captcha, no email entry.
  const mailResults = async () => {
    setEmailing(true);
    setError(null);
    try {
      await apiRequest('POST', `/bravos/instrument/${key}/email-results`, {});
      setEmailed(true);
    } catch {
      setError(t('emailResultsError'));
    } finally {
      setEmailing(false);
    }
  };

  // Retake: drop the shown result and re-open the questionnaire.
  const retake = async () => {
    setStage('loading');
    setResult(null);
    setJustSubmitted(false);
    setEmailed(false);
    setError(null);
    setAnswers({});
    try {
      await loadQuestions();
      setStage('questions');
    } catch {
      setError(t('somethingWentWrong')); setStage('results');
    }
  };

  // Remove results: erase the member's stored answers for this pulse (privacy). Their input also
  // leaves the company consolidation. Afterwards the pulse reads as un-taken — re-open it fresh.
  const removeResults = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('removeResultsConfirm'))) return;
    setRemoving(true);
    setError(null);
    try {
      await apiRequest('POST', `/bravos/instrument/${key}/remove-result`, {});
      setResult(null);
      setJustSubmitted(false);
      setEmailed(false);
      setAnswers({});
      setStage('loading');
      await loadQuestions();
      setStage('questions');
    } catch {
      setError(t('removeResultsError')); setStage('results');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="list-check" theme="primary" className="me-2" />
          {meta ? meta.name : t('yourAssessments')}
        </Title>

        {stage === 'loading' && <span className="auth__spinner" />}

        {stage === 'questions' && meta && (
          <BravosSurveyFlow
            questions={meta.questions}
            interstitials={meta.interstitials}
            answers={answers}
            setAnswer={setAnswer}
            onComplete={computeResults}
            header={<p className="bravos-q__hint">{t('assessmentAnonymousNote')}</p>}
          />
        )}

        {stage === 'working' && <WorkingOnFeedback />}

        {stage === 'results' && (
          result && result.feedback ? (
            <>
              {justSubmitted && (
                <div className="bravos-recorded">
                  <Icon name="check" /> {t('assessmentRecorded')}
                </div>
              )}
              <MemberResults t={t} feedback={result.feedback} />
              {emailed ? (
                <p className="text-success mt-2">
                  <Icon name="check" className="me-1" />{t('pulseEmailedNote')}
                </p>
              ) : (
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        onClick={mailResults} disabled={emailing}>
                  {emailing ? <span className="auth__spinner" /> : <Icon name="envelope" />}
                  {t('pulseEmailNow')}
                </button>
              )}
              {error && <p className="text-danger mt-2">{error}</p>}
              <button type="button" className="bravos-submit bravos-submit--ghost" onClick={retake}>
                <Icon name="redo" /> {t('retakeTest')}
              </button>
              {/* Erase my stored results — the privacy escape hatch, right next to Retake. */}
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={removeResults} disabled={removing}>
                {removing ? <span className="auth__spinner" /> : <Icon name="trash" />}
                {' '}{t('removeResults')}
              </button>
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-danger">{error || t('somethingWentWrong')}</p>
              <button type="button" className="bravos-submit" onClick={retake}>
                <Icon name="redo" /> {t('retakeTest')}
              </button>
            </div>
          )
        )}
      </Card>
    </Base>
  );
};

const MemberResults = ({ t, feedback }) => (
  <div className="bravos-feedback">
    {(feedback.blocks || []).map((b, i) => (
      <div key={i} className="bravos-fb-block">
        {b.bucket && b.bucket !== 'OVERALL' && <p className="bravos-fb-sub">{b.bucket}</p>}
        {b.feedback && <p className="bravos-fb-block__text">{b.feedback}</p>}
      </div>
    ))}
    {feedback.focus && (
      <div className="bravos-fb-focus">
        <p className="bravos-fb-focus__title">
          {t('focusArea')}: {feedback.focus.bucket}
        </p>
        {feedback.focus.diagnostic && (
          <p className="bravos-fb-block__text">{feedback.focus.diagnostic}</p>
        )}
        {/* No 'Bravos Value' line here: that copy is the lead magnet for the PUBLIC pulses
            ("this is what Bravos would do for you"). The reader of this page is already a
            member, so the focus area shows the diagnostic only. */}
      </div>
    )}
  </div>
);

export default BravosSurvey;
